import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, ChevronDown, Camera, AlertCircle, CheckCircle2 } from 'lucide-react';
import './OpenMarketUploadProductRequest.css';
import { db } from '../../services/firebase';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { useBuyer } from '../../contexts/BuyerContext';
import PageHeader from '../../components/PageHeader';
import productData from '../../SELLERPRODUCT.json';

const OpenMarketUploadProductRequest = () => {
  const { buyer, refreshSegmentData } = useBuyer();
  const buyerId = buyer?.buyerId || buyer?.uid || buyer?.id || null;


  useEffect(() => {
    if (refreshSegmentData) refreshSegmentData();
  }, [refreshSegmentData]);
  // ═══════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════
  const [formData, setFormData] = useState({
    segment: buyer?.segment || '',
    category: '',
    productName: '',
    styleType: '',
    specification: '',
    productSource: '',
    paymentMethod: 'RTGS',
    buyerNotes: '',
  });

  const [specData, setSpecData] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [lotDrawerOpen, setLotDrawerOpen] = useState(false);

  const sizeRowRefs = useRef({});
  const setSizeRowRef = (index, element) => {
    if (element) sizeRowRefs.current[index] = element;
  };

  const [lotSizes, setLotSizes] = useState([
    { size: '', set: '', minNetWt: '', maxNetWt: '' }
  ]);

  // ═══════════════════════════════════════
  // DERIVED DATA FROM JSON (ONLY REGISTERED SEGMENTS)
  // ═══════════════════════════════════════
  const segments = useMemo(() => {
    const registeredSet = new Set();

    if (buyer?.operatorsBySegment) {
      Object.keys(buyer.operatorsBySegment).forEach((seg) => {
        if (buyer.operatorsBySegment[seg]) {
          registeredSet.add(seg.toUpperCase().trim());
        }
      });
    }

    if (buyer?.sellersBySegment) {
      Object.keys(buyer.sellersBySegment).forEach((seg) => {
        registeredSet.add(seg.toUpperCase().trim());
      });
    }

    if (buyer?.segment) {
      registeredSet.add(buyer.segment.toUpperCase().trim());
    }

    const allJsonSegments = Object.keys(productData);
    return allJsonSegments.filter((seg) => registeredSet.has(seg.toUpperCase().trim()));
  }, [buyer?.operatorsBySegment, buyer?.sellersBySegment, buyer?.segment]);

  // Keep segment in sync with registered segments
  useEffect(() => {
    if (segments.length > 0) {
      if (!formData.segment || !segments.includes(formData.segment)) {
        setFormData(prev => ({
          ...prev,
          segment: segments[0],
          category: '',
          productSource: '',
          productName: '',
          styleType: '',
          specification: ''
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        segment: '',
        category: '',
        productSource: '',
        productName: '',
        styleType: '',
        specification: ''
      }));
    }
  }, [segments,formData.segment]);

  const categoriesForSegment = useMemo(() => {
    if (!formData.segment) return [];
    return Object.keys(productData[formData.segment.toUpperCase()] || {});
  }, [formData.segment]);

  const sourcesForCategory = useMemo(() => {
    if (!formData.segment || !formData.category) return [];
    return Object.keys(
      productData[formData.segment.toUpperCase()]?.[formData.category] || {}
    );
  }, [formData.segment, formData.category]);

  const productsForSource = useMemo(() => {
    if (!formData.segment || !formData.category || !formData.productSource) return [];
    return Object.keys(
      productData[formData.segment.toUpperCase()]?.[formData.category]?.[formData.productSource] || {}
    );
  }, [formData.segment, formData.category, formData.productSource]);

  const productInfo = useMemo(() => {
    if (!formData.segment || !formData.category || !formData.productSource || !formData.productName) return null;
    return productData[formData.segment.toUpperCase()]?.[formData.category]?.[formData.productSource]?.[formData.productName] || null;
  }, [formData.segment, formData.category, formData.productSource, formData.productName]);

  const finishTypes = useMemo(() => {
    if (!productInfo?.finishTypes) return [];
    return Object.keys(productInfo.finishTypes);
  }, [productInfo]);

  const specificationsForFinish = useMemo(() => {
    if (!productInfo?.finishTypes || !formData.styleType) return [];
    return productInfo.finishTypes[formData.styleType] || [];
  }, [productInfo, formData.styleType]);

  const sizeOptions = useMemo(() => productInfo?.options || [], [productInfo]);
  const sizeUnit = useMemo(() => productInfo?.sizing_unit || '', [productInfo]);

 
  // ═══════════════════════════════════════
  // CLOUDINARY CONFIG
  // ═══════════════════════════════════════
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const CLOUDINARY_UPLOAD_PRESET = "jmiseller";
  const CLOUDINARY_API_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;

  // ═══════════════════════════════════════
  // ALERT
  // ═══════════════════════════════════════
  const showAlertMessage = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };

  // ═══════════════════════════════════════
  // SPEC FETCH — on complete product selection
  // ═══════════════════════════════════════
  useEffect(() => {
    if (
      formData.segment &&
      formData.category &&
      formData.productName &&
      formData.styleType &&
      formData.specification &&
      formData.productSource
    ) {
      fetchSpecData();
    } else {
      setSpecData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.segment, formData.category, formData.productName, formData.styleType, formData.specification, formData.productSource]);

  const fetchSpecData = async () => {
    try {
      const q = query(
        collection(db, 'productSpecs'),
        where('segment', '==', formData.segment.toUpperCase()),
        where('category', '==', formData.category),
        where('productName', '==', formData.productName)
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const allSpecs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        const norm = (v) => String(v || '').toLowerCase().trim();

        // Most specific: all 6 fields match
        const exactMatch = allSpecs.find(s =>
          norm(s.segment) === norm(formData.segment) &&
          norm(s.category) === norm(formData.category) &&
          norm(s.productName) === norm(formData.productName) &&
          norm(s.productSource) === norm(formData.productSource) &&
          norm(s.finishType) === norm(formData.styleType) &&
          norm(s.specification) === norm(formData.specification)
        );

        // Fallback: without specification
        const finishMatch = allSpecs.find(s =>
          norm(s.productSource) === norm(formData.productSource) &&
          norm(s.finishType) === norm(formData.styleType)
        );

        // Fallback: productSource only
        const sourceMatch = allSpecs.find(s =>
          norm(s.productSource) === norm(formData.productSource)
        );

        const matched = exactMatch || finishMatch || sourceMatch || null;

        if (matched) {
          setSpecData(matched);
        } else {
          setSpecData(null);
          showAlertMessage('No specification configuration found for this combination.', 'error');
        }
      } else {
        setSpecData(null);
        showAlertMessage('No specification found for this product.', 'error');
      }
    } catch (err) {
      console.error('Error fetching spec:', err);
      setSpecData(null);
    }
  };

  // ═══════════════════════════════════════
  // LOT CALCULATIONS
  // ═══════════════════════════════════════
  const calculateItemAverages = (item) => {
    const minN = parseFloat(item.minNetWt) || 0;
    const maxN = parseFloat(item.maxNetWt) || 0;
    const avgNetWt = ((minN + maxN) / 2).toFixed(3);
    return {
      avgNetWt,
      avgGrossWt: '0.000',
      avgSpecWt: '0.000',
      grossWt: '0',
      netWt: '0'
    };
  };

  const calculateTotals = () => {
    return {
      totalSets: lotSizes.reduce((sum, item) => sum + (parseFloat(item.set) || 0), 0),
      totalGrossWt: lotSizes.reduce((sum, item) => sum + (parseFloat(item.avgGrossWt) * (parseFloat(item.set) || 0)), 0),
      totalNetWt: lotSizes.reduce((sum, item) => sum + (parseFloat(item.avgNetWt) * (parseFloat(item.set) || 0)), 0)
    };
  };

  const updateLotSizesWithAverages = (sizes) => {
    return sizes.map(item => {
      const averages = calculateItemAverages(item);
      return { ...item, ...averages };
    });
  };

  const totals = calculateTotals();

  // ═══════════════════════════════════════
  // LOT DRAWER HANDLERS
  // ═══════════════════════════════════════
  const handleAddLotSize = () => {
    if (lotSizes.length >= sizeOptions.length) {
      showAlertMessage('All available sizes have been added.', 'info');
      return;
    }
    setLotSizes([
      ...lotSizes,
      { size: '', set: '', minNetWt: '', maxNetWt: '' }
    ]);
  };

  const handleSizeChange = (index, field, value) => {
    if (field === 'size') {
      const dup = lotSizes.findIndex((item, i) => i !== index && item.size === value);
      if (dup !== -1) {
        if (sizeRowRefs.current[dup]) {
          sizeRowRefs.current[dup].scrollIntoView({ behavior: 'smooth', block: 'center' });
          sizeRowRefs.current[dup].classList.add('duplicate-highlight');
          setTimeout(() => sizeRowRefs.current[dup]?.classList.remove('duplicate-highlight'), 2000);
        }
        return;
      }
    }

    const newSizes = [...lotSizes];
    newSizes[index] = { ...newSizes[index], [field]: value };

    if (field === 'minNetWt' || field === 'maxNetWt') {
      const minN = parseFloat(field === 'minNetWt' ? value : newSizes[index].minNetWt);
      const maxN = parseFloat(field === 'maxNetWt' ? value : newSizes[index].maxNetWt);
      if (!isNaN(minN) && !isNaN(maxN) && minN > maxN) {
        showAlertMessage('Min Net Wt. per set cannot exceed Max Net Wt. per set.');
        return;
      }
    }

    if (field === 'set' || field === 'minNetWt' || field === 'maxNetWt') {
      const averages = calculateItemAverages(newSizes[index]);
      newSizes[index] = { ...newSizes[index], ...averages };
    }

    setLotSizes(newSizes);
  };

  const handleSaveLot = () => {
    const updated = updateLotSizesWithAverages(lotSizes);
    setLotSizes(updated);
    setLotDrawerOpen(false);
  };

  // ═══════════════════════════════════════
  // FORM INPUT HANDLER (cascading reset)
  // ═══════════════════════════════════════
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updates = { [field]: value };
      if (field === 'segment') { updates.category = ''; updates.productSource = ''; updates.productName = ''; updates.styleType = ''; updates.specification = ''; }
      if (field === 'category') { updates.productSource = ''; updates.productName = ''; updates.styleType = ''; updates.specification = ''; }
      if (field === 'productSource') { updates.productName = ''; updates.styleType = ''; updates.specification = ''; }
      if (field === 'productName') { updates.styleType = ''; updates.specification = ''; }
      if (field === 'styleType') { updates.specification = ''; }
      return { ...prev, ...updates };
    });
    setSpecData(null);
    setLotSizes([{ size: '', set: '', minNetWt: '', maxNetWt: '' }]);
  };

  // ═══════════════════════════════════════
  // IMAGE UPLOAD
  // ═══════════════════════════════════════
  const handleImageUpload = async (files) => {
    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      try {
        const response = await fetch(CLOUDINARY_API_URL, { method: 'POST', body: fd });
        const data = await response.json();
        return { id: data.public_id, url: data.secure_url, name: file.name };
      } catch (error) {
        console.error('Upload failed:', error);
        return null;
      }
    });
    const results = await Promise.all(uploadPromises);
    const successful = results.filter(Boolean);
    setUploadedImages(prev => [...prev, ...successful]);
    setUploading(false);
  };

  const removeImage = (id) => {
    setUploadedImages(prev => prev.filter(img => img.id !== id));
    if (mainImageIndex >= uploadedImages.length - 1) setMainImageIndex(0);
  };

  // ═══════════════════════════════════════
  // SUBMIT
  // ═══════════════════════════════════════
  const handleSubmit = async () => {
    if (!buyerId) { showAlertMessage('Buyer not authenticated.'); return; }
    if (!formData.segment || !formData.category || !formData.productName || !formData.styleType || !formData.specification || !formData.productSource) {
      showAlertMessage('Please fill all product information fields.'); return;
    }
    if (!specData) { showAlertMessage('Specification not loaded. Please wait or reselect product.'); return; }
    if (!uploadedImages.length) { showAlertMessage('Please upload at least one image.'); return; }
    if (lotSizes.length === 0 || !lotSizes.every(item => item.size && item.set && item.minNetWt && item.maxNetWt)) {
      showAlertMessage('Please fill all lot details with size, set, min net wt, and max net wt.'); return;
    }

    setSubmitting(true);
    try {
      // Strip minNetWt & maxNetWt — NOT stored in DB
      const finalLotDetails = updateLotSizesWithAverages(lotSizes).map(item => ({
        size: item.size,
        set: item.set,
        grossWt: '0',
        netWt: '0',
        avgGrossWt: '0.000',
        avgNetWt: item.avgNetWt,
        avgSpecWt: '0.000'
      }));

      const num = (v) => String(v ?? '');
      const spec = specData;

      // Derive operatorId for the selected segment
      const segmentKey = formData.segment?.toLowerCase?.() || '';
      const operatorId =
        buyer?.operatorsBySegment?.[segmentKey] ||
        buyer?.operatorsBySegment?.[formData.segment] ||
        buyer?.operatorsBySegment?.[formData.segment?.toUpperCase()] ||
        null;

      const productDataToSave = {
        // Identification
        buyerId: buyerId,
        operatorId: operatorId || '',
        // Product Information
        segment: formData.segment.toUpperCase(),
        category: formData.category,
        productName: formData.productName,
        styleType: formData.styleType,
        finishType: spec.finishType || formData.styleType,
        specification: formData.specification,
        productSource: formData.productSource,
        // 6 Spec Values — MAX Applied
        buyerDeskCalculation: num(spec.buyerDeskCalculation),
        wastage: num(spec.maxWastageSeller),
        setMc: num(spec.maxSetMakingSeller),
        netGramMc: num(spec.maxGramMakingSeller),
        specificationMC: num(spec.maxSpecificationMakingSeller),
        specificationGramRate: num(spec.maxSpecificationGramRateSeller),
        // JMI Multipliers
        gramMakingJmi: num(spec.gramMakingJmi),
        setMakingJmi: num(spec.setMakingJmi),
        wastageJmi: num(spec.wastageJmi),
        specificationMakingJmi: num(spec.specificationMakingJmi),
        specificationGramRateJmi: num(spec.specificationGramRateJmi),
        // Operator Multipliers
        gramMakingOperator: num(spec.gramMakingOperator),
        setMakingOperator: num(spec.setMakingOperator),
        wastageOperator: num(spec.wastageOperator),
        specificationMakingOperator: num(spec.specificationMakingOperator),
        specificationGramRateOperator: num(spec.specificationGramRateOperator),
        // Raw Max Constraints (reference)
        maxWastageSeller: num(spec.maxWastageSeller),
        maxGramMakingSeller: num(spec.maxGramMakingSeller),
        maxSetMakingSeller: num(spec.maxSetMakingSeller),
        maxSpecificationMakingSeller: num(spec.maxSpecificationMakingSeller),
        maxSpecificationGramRateSeller: num(spec.maxSpecificationGramRateSeller),
        // SLA
        maxReadyServeSla: num(spec.maxReadyServeSla),
        orderServeSla: num(spec.orderServeSla),
        // Images
        images: uploadedImages,
        // Service & Status
        serviceType: 'openmarket',
        status: 'requested',
        // Buyer Notes
        buyerNotes: formData.buyerNotes,
        // Lot Details — WITHOUT minNetWt/maxNetWt
        lotDetails: finalLotDetails,
        // Payment
        paymentMethod: formData.paymentMethod,
        // Timestamps
        timestamp: new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      }; 
      console.log(productDataToSave)
      await addDoc(collection(db, 'products'), productDataToSave);
      showAlertMessage('Open Market request submitted successfully!', 'success');

      // Reset
      setUploadedImages([]);
      setMainImageIndex(0);
      setSpecData(null);
      setLotSizes([{ size: '', set: '', minNetWt: '', maxNetWt: '' }]);
      setFormData({
        segment: buyer?.segment || '',
        category: '',
        productName: '',
        styleType: '',
        specification: '',
        productSource: '',
        paymentMethod: 'RTGS',
        buyerNotes: '',
      });
    } catch (error) {
      console.error('Error saving:', error);
      showAlertMessage('Error saving request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════
  const num = (v) => parseFloat(v) || 0;
  const generateTitle = () => `${formData.segment || ''}-${formData.category || ''}-${formData.productName || ''}-${formData.styleType || ''}-${formData.specification || ''}`;

  // ═══════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════
  return (
    <>
      <PageHeader title='Open Market Request' />
      <div className="open-market-request-container">

        {/* Alert */}
        {showAlert && (
          <div className={`alert-message alert-${alertType}`}>
            <div className="alert-content">
              {alertType === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
              <span className="alert-text">{alertMessage}</span>
              <button className="alert-close" onClick={() => setShowAlert(false)}>×</button>
            </div>
          </div>
        )}

       
          <div className="content">

          {/* ─── PRODUCT INFORMATION ─── */}
          <div className="section">
            <h3 className="section-title">Product Information</h3>
            <div className="form-grid-3">
              {/* Segment */}
              <div className="formfield">
                <label className="form-label">Segment *</label>
                <div className="select-wrapper">
                  <select
                    value={formData.segment}
                    onChange={(e) => handleInputChange('segment', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select Segment</option>
                    {segments.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="select-icon" />
                </div>
              </div>

              {/* Category */}
              <div className="formfield">
                <label className="form-label">Category *</label>
                <div className="select-wrapper">
                  <select value={formData.category} onChange={(e) => handleInputChange('category', e.target.value)} className="form-select" disabled={!formData.segment}>
                    <option value="">Select Category</option>
                    {categoriesForSegment.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <ChevronDown className="select-icon" />
                </div>
              </div>

              {/* Source */}
              <div className="formfield">
                <label className="form-label">Source *</label>
                <div className="select-wrapper">
                  <select value={formData.productSource} onChange={(e) => handleInputChange('productSource', e.target.value)} className="form-select" disabled={!formData.category}>
                    <option value="">Select Source</option>
                    {sourcesForCategory.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="select-icon" />
                </div>
              </div>

              {/* Product Name */}
              <div className="formfield">
                <label className="form-label">Product *</label>
                <div className="select-wrapper">
                  <select value={formData.productName} onChange={(e) => handleInputChange('productName', e.target.value)} className="form-select" disabled={!formData.productSource}>
                    <option value="">Select Product</option>
                    {productsForSource.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown className="select-icon" />
                </div>
              </div>

              {/* Style Type / Finish Type */}
              <div className="formfield">
                <label className="form-label">Style Type *</label>
                <div className="select-wrapper">
                  <select value={formData.styleType} onChange={(e) => handleInputChange('styleType', e.target.value)} className="form-select" disabled={!formData.productName}>
                    <option value="">Select Style Type</option>
                    {finishTypes.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <ChevronDown className="select-icon" />
                </div>
              </div>

              {/* Specification */}
              <div className="formfield">
                <label className="form-label">Specification *</label>
                <div className="select-wrapper">
                  <select value={formData.specification} onChange={(e) => handleInputChange('specification', e.target.value)} className="form-select" disabled={!formData.styleType}>
                    <option value="">Select Specification</option>
                    {specificationsForFinish.map(sp => <option key={sp} value={sp}>{sp}</option>)}
                  </select>
                  <ChevronDown className="select-icon" />
                </div>
              </div>
            </div>
          </div>
          {/* ─── UPLOAD PHOTOS ─── */}
          <div className="section">
            <h3 className="section-title">Upload Photos</h3>
            {uploadedImages.length > 0 ? (
              <div className="image-upload-container">
                <div className="main-image-container">
                  <img src={uploadedImages[mainImageIndex]?.url} alt="Main" className="main-image" />
                </div>
                <div className="thumbnail-grid">
                  {uploadedImages.map((image, index) => (
                    <div key={image.id} className={`thumbnail-container ${index === mainImageIndex ? 'active' : ''}`} onClick={() => setMainImageIndex(index)}>
                      <img src={image.url} alt={image.name} className="thumbnail-image" />
                      <button onClick={(e) => { e.stopPropagation(); removeImage(image.id); }} className="remove-thumbnail-btn">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="upload-area">
                <div className="upload-icon-container"><Camera className="upload-icon" /></div>
                <p className="upload-text">{generateTitle() || 'Upload product photos'}</p>
                <input type="file" multiple accept="image/*" onChange={(e) => handleImageUpload(e.target.files)} className="upload-input" id="photo-upload" disabled={uploading} />
                <label htmlFor="photo-upload" className={`upload-button ${uploading ? 'loading' : ''}`}>
                  {uploading ? 'Uploading...' : 'Add Photos'}
                </label>
              </div>
            )}
            {uploadedImages.length > 0 && (
              <div className="upload-area" style={{ marginTop: '0.75rem' }}>
                <input type="file" multiple accept="image/*" onChange={(e) => handleImageUpload(e.target.files)} className="upload-input" id="more-photos" disabled={uploading} />
                <label htmlFor="more-photos" className={`upload-button ${uploading ? 'loading' : ''}`}>
                  {uploading ? 'Uploading...' : 'Add More Photos'}
                </label>
              </div>
            )}
          </div>

          {/* ─── LOT DETAILS ─── */}
          {formData.productName && sizeOptions.length > 0 && (
            <div className="section">
              <h3 className="section-title">Lot Details {sizeUnit && `(${sizeUnit})`}</h3>
              <div className="lot-summary" onClick={() => setLotDrawerOpen(true)}>
                <div className="lot-preview">
                  <table className="lot-table">
                    <thead>
                      <tr>
                        <th>Size</th>
                        <th>Set</th>
                        <th>Min Net/Set</th>
                        <th>Max Net/Set</th>
                        <th>Avg Net/Set{formData.specification !== 'PLANE' ? ` (${formData.specification})` : ''}</th>
                        <th>Your Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lotSizes.map((item, i) => (
                        <tr key={i}>
                          <td>{item.size || '-'}</td>
                          <td>{item.set || '0'}</td>
                          <td>{item.minNetWt || '0.000'}</td>
                          <td>{item.maxNetWt || '0.000'}</td>
                          <td>{item.avgNetWt || '0.000'}</td>
                          <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                            {item.minNetWt && item.maxNetWt ? `${item.minNetWt} - ${item.maxNetWt} g/set` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="totals-row">
                        <td>Total</td>
                        <td>{totals.totalSets}</td>
                        <td>{totals.totalGrossWt.toFixed(3)}</td>
                        <td>{totals.totalNetWt.toFixed(3)}</td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="click-to-edit">Click to edit lot details</div>
                </div>
              </div>
            </div>
          )}

          {/* ─── BUYER NOTES ─── */}
          <div className="section">
            <h3 className="section-title">Buyer Notes</h3>
            <textarea
              value={formData.buyerNotes}
              onChange={(e) => setFormData(prev => ({ ...prev, buyerNotes: e.target.value }))}
              className="form-input"
              placeholder="Any specific requirements, preferences, or notes for sellers..."
              rows={3}
              style={{ resize: 'vertical', width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
            />
          </div>

          {/* ─── PAYMENT METHOD ─── */}
          <div className="section">
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }}>
              {['RTGS', 'Metal'].map((method) => (
                <label key={method} htmlFor={`pay-${method}`} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '0.5rem', fontSize: '0.95rem' }}>
                  <input type="radio" name="paymentMethod" id={`pay-${method}`} value={method} checked={formData.paymentMethod === method} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} style={{ display: 'none' }} />
                  <span style={{
                    width: '20px', height: '20px', borderRadius: '50%', border: '2px solid #c49b27',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: formData.paymentMethod === method ? '#c49b27' : 'transparent',
                    transition: 'all 0.25s ease'
                  }}>
                    {formData.paymentMethod === method && <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />}
                  </span>
                  {method}
                </label>
              ))}
            </div>
          </div>

          {/* ─── SUBMIT ─── */}
          <button
            onClick={handleSubmit}
            disabled={submitting || !specData}
            className="submit-button"
          >
            {submitting ? 'Submitting...' : 'Submit Open Market Request'}
          </button>
        </div>
       

        {/* ═══════════════════════════════════════════════ */}
        {/* LOT DRAWER                                      */}
        {/* ═══════════════════════════════════════════════ */}
        {lotDrawerOpen && (
          <div className="lot-drawer-overlay" onClick={() => setLotDrawerOpen(false)}>
            <div className="lot-drawer" onClick={e => e.stopPropagation()}>
              <div className="drawer-header">
                <h3>Lot Details {sizeUnit && `(${sizeUnit})`}</h3>
                <button className="close-btn" onClick={() => setLotDrawerOpen(false)}>×</button>
              </div>

              <div className="om-lot-size-section">
                {lotSizes.map((item, index) => (
                  <div key={index} className="om-lot-size-row" ref={el => setSizeRowRef(index, el)} data-row-index={index}>
                    <div className="om-lot-size-grid">
                      <div className="om-lot-size-cell">
                        <label className="om-lot-size-label">Size</label>
                        <div className="om-lot-select-wrap">
                          <select className="om-lot-size-select" value={item.size} onChange={(e) => handleSizeChange(index, 'size', e.target.value)}>
                            <option value="">Select</option>
                            {sizeOptions.filter(s => !lotSizes.some((it, i) => i !== index && it.size === s)).map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          <ChevronDown className="om-lot-select-icon" />
                        </div>
                      </div>

                      <div className="om-lot-size-cell">
                        <label className="om-lot-size-label">Set</label>
                        <input className="om-lot-size-input" type="number" value={item.set} onChange={(e) => handleSizeChange(index, 'set', e.target.value)} placeholder="0" />
                      </div>
                      <div className="om-lot-size-cell">
                        <label className="om-lot-size-label">Max Net/Set (g)</label>
                        <input className="om-lot-size-input" type="number" step="0.001" value={item.maxNetWt} onChange={(e) => handleSizeChange(index, 'maxNetWt', e.target.value)} placeholder="Max" />
                      </div>
                      <div className="om-lot-size-cell">
                        <label className="om-lot-size-label">Min Net/Set (g)</label>
                        <input className="om-lot-size-input" type="number" step="0.001" value={item.minNetWt} onChange={(e) => handleSizeChange(index, 'minNetWt', e.target.value)} placeholder="Min" />
                      </div>


                    </div>

                    {lotSizes.length > 1 && (
                      <button type="button" className="om-lot-remove-btn" onClick={() => setLotSizes(lotSizes.filter((_, i) => i !== index))}>×</button>
                    )}
                  </div>
                ))}

                <button type="button" onClick={handleAddLotSize} className="om-lot-add-btn" disabled={lotSizes.length >= sizeOptions.length}>
                  + Add Size
                </button>
              </div>

              <div className="drawer-footer">
                <button className="cancel-btn" onClick={() => setLotDrawerOpen(false)}>Cancel</button>
                <button
                  className="save-btn"
                  onClick={handleSaveLot}
                  disabled={!lotSizes.every(item => item.size && item.set && item.minNetWt && item.maxNetWt && num(item.minNetWt) <= num(item.maxNetWt))}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OpenMarketUploadProductRequest;